import React, { useContext, useState, useMemo } from 'react';

import { Row, Col } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { getMoment } from '@libs/NwMoment';
import { datePickerBeforeOnChange } from '@libs/NwMoment';
import { capitalize } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';
import { useUserPermission } from '@contexts/UserContext';

import { useAddAgency, useEditAgency } from '@hooks/API/Contact/UseContactAgencies';
import { getCustomer, useModelsList } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import {
    NWContactDropdown,
    NWSelect,
    NWInput,
    NWInputNumber,
    NWTextAreaCharCounter,
    NWDatePicker,
    NWSwitch,
    NWAgencyTypeSelector,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const ModelAgencyForm = ({
    modelAgency,
    contactID,
    agencyID,
    hideMother,
    exclusions = [],
    excludedModels,
    onCancel,
    onSave,
    onTouchForm,
}) => {
    const accessAccounting = useUserPermission('AccessAccounting');

    const configurations = useConfigurations();
    const motherAgencyPercentageInConfig = configurations.defaultMotherAgencyCommissionPerc;
    const motherAgencyPercentageOnGrossInConfig = configurations.defaultMotherAgencyCommissionPercOnGross;

    const { cachedList } = useContext(ListContext);
    const [isDropped, setIsDropped] = useState(modelAgency ? modelAgency.Dropped : false);

    const modelAgencyLabels = cachedList.modelAgencyLabels.items;
    const newEnter = modelAgency ? false : true;
    const { data: modelList } = useModelsList();
    let disableMother = hideMother;

    // Determine context: agency perspective (selecting model) vs model perspective (selecting agency)
    const isAgencyPerspective = !contactID && agencyID;

    const { mutateAsync: onAddAgency } = useAddAgency();
    const { mutateAsync: onEditAgency } = useEditAgency();

    const initialValues = {
        TypeName: hideMother ? 'Other' : 'Mother',
        DateIn: null,
        DateOut: null,
        ContractDate: null,
        OnGross: motherAgencyPercentageOnGrossInConfig,
        Dropped: false,
        ModelAgencyLabelID: null,
        Notes: '',
        AccountingNotes: '',
        Booker: '',
        Percentage: hideMother ? 0 : motherAgencyPercentageInConfig,
        Towns: [],
        ModelID: contactID || null,
        AgencyID: agencyID || null,
    };

    if (modelAgency) {
        initialValues.AgencyID = modelAgency.AgencyID;
        initialValues.ID = modelAgency.ID;
        initialValues.TypeName = modelAgency.TypeName;
        initialValues.DateIn = modelAgency.DateIn;
        initialValues.DateOut = modelAgency.DateOut;
        initialValues.ContractDate = modelAgency.ContractDate;
        initialValues.OnGross = modelAgency.OnGross;
        initialValues.Dropped = modelAgency.Dropped;
        // In agency perspective, ModelID comes from nested Model object
        if (isAgencyPerspective && modelAgency.Model) {
            initialValues.ModelID = modelAgency.Model.ID;
        }
        initialValues.ModelAgencyLabelID =
            modelAgencyLabels.findIndex(
                (label) => label.ModelAgencyType === modelAgency.TypeName && label.ID === modelAgency.ModelAgencyLabelID
            ) >= 0
                ? modelAgency.ModelAgencyLabelID
                : null;
        initialValues.Notes = modelAgency.Notes || '';
        initialValues.AccountingNotes = modelAgency.AccountingNotes || '';
        initialValues.Percentage = modelAgency.Percentage;
        initialValues.Booker = modelAgency.Booker || null;
        initialValues.Towns = modelAgency.Towns
            ? modelAgency.Towns.split(',') // Data comes in this format: "City1,City2,..."
            : [];
    }

    // Merge cities from cachedList with existing cities
    const cities = Array.from([
        ...new Set([...cachedList.cities.items.map((city) => city.Name), ...initialValues.Towns]),
    ]);

    const getDefaultCity = (ID) => {
        const currentCustomer = getCustomer(Number(ID));
        return currentCustomer.C;
    };

    const getAgencyDefaults = (ID) => {
        if (ID) {
            const customer = getCustomer(Number(ID));
            if (customer && customer.DC && customer.DC.length > 0) {
                return {
                    Percentage: customer.DC[0].MAC !== null ? customer.DC[0].MAC : motherAgencyPercentageInConfig,
                    OnGross:
                        customer.DC[0].MACOG !== null ? customer.DC[0].MACOG : motherAgencyPercentageOnGrossInConfig,
                };
            }
        }
        return {
            Percentage: motherAgencyPercentageInConfig,
            OnGross: motherAgencyPercentageOnGrossInConfig,
        };
    };

    // When agencyID is provided (both perspectives), set agency defaults
    if (!modelAgency && agencyID) {
        const agdef = getAgencyDefaults(agencyID);
        initialValues.Percentage = agdef.Percentage;
        initialValues.OnGross = agdef.OnGross;
        const defaultCity = getDefaultCity(agencyID);
        if (defaultCity) {
            initialValues.Towns = [defaultCity];
        }
    }

    // if (modelAgency && modelAgency.AgencyID) {
    //     initialValues.AgencyID = modelAgency.AgencyID;
    //     const agdef = getAgencyDefaults(modelAgency.AgencyID);
    //     initialValues.Percentage = agdef.Percentage;
    //     initialValues.OnGross = agdef.OnGross;
    // }

    const saveModelAgencies = async (values, setSubmitting) => {
        const ID = initialValues.ID;
        const agencyPercentage = values.TypeName === 'Placement' ? 0 : values.Percentage;
        const agencyOnGross = values.TypeName === 'Placement' ? true : values.OnGross;

        const data = {
            ModelID: values.ModelID,
            AgencyID: values.AgencyID,
            Type: values.TypeName,
            DateIn: values.DateIn,
            DateOut: values.DateOut,
            ContractDate: values.ContractDate,
            OnGross: agencyOnGross,
            Dropped: values.Dropped,
            ModelAgencyLabelID: values.ModelAgencyLabelID,
            Notes: values.Notes,
            AccountingNotes: values.AccountingNotes,
            Booker: values.Booker,
            Percentage: agencyPercentage,
            Towns: values.Towns.join(','),
        };

        if (ID) {
            data.ID = ID;
            await onEditAgency(data);
            onTouchForm(false);
            setSubmitting(false);
            onSave();
        } else {
            await onAddAgency(data);
            onTouchForm(false);
            setSubmitting(false);
            onSave();
        }
    };

    return (
        <div style={{ margin: '20px 0' }}>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    saveModelAgencies(values, setSubmitting);
                }}
                validationSchema={Yup.object().shape({
                    TypeName: Yup.mixed().required('Content is required'),
                    ModelID: Yup.string().required('Content is required'),
                    AgencyID: Yup.string().required('Content is required'),
                    DateOut: Yup.date()
                        .nullable()
                        .when('DateIn', (DateIn, schema) =>
                            DateIn ? schema.min(DateIn, 'DATE OUT must be greater than DATE IN') : schema
                        ),
                })}
            >
                {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                    <NwForm values={values} onTouchForm={onTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Field
                            component={NWAgencyTypeSelector}
                            label="Type"
                            name="TypeName"
                            required
                            value={values.TypeName}
                            hideMother={disableMother}
                            onAfterChange={(value) => {
                                setFieldValue('ModelAgencyLabelID', null);
                                if (value === 'Mother' && !values.Dropped) {
                                    const agdef = getAgencyDefaults(values.AgencyID);
                                    setFieldValue('Percentage', agdef.Percentage);
                                    setFieldValue('OnGross', agdef.OnGross);
                                } else {
                                    setFieldValue('Percentage', 0);
                                    setFieldValue('OnGross', true);
                                }
                            }}
                        />
                        <Field
                            component={NWSelect}
                            label="Label"
                            name="ModelAgencyLabelID"
                            allowClear={true}
                            value={values.ModelAgencyLabelID}
                            options={modelAgencyLabels
                                .filter((label) => label.ModelAgencyType === values.TypeName)
                                .map((el) => {
                                    return { key: el.ID, label: el.Name, value: el.ID };
                                })}
                        />

                        {/* AgencyID */}
                        <Row gutter={10}>
                            <Col lg={16} md={16} sm={12} xs={24}>
                                {!agencyID && (
                                    <Field
                                        component={NWContactDropdown}
                                        label="Agency"
                                        disabled={!newEnter}
                                        exclusions={exclusions}
                                        name="AgencyID"
                                        activeTypes={['age']}
                                        activableTypes={['cli', 'srv']}
                                        value={values.AgencyID}
                                        onAfterChange={(value) => {
                                            // If the agency has a default city, add it to towns
                                            const defaultCity = getDefaultCity(value);
                                            if (defaultCity) {
                                                //if not present in value, then add it
                                                setFieldValue('Towns', [defaultCity]);
                                            } else {
                                                setFieldValue('Towns', []);
                                            }
                                            if (values.TypeName === 'Mother' && !values.Dropped) {
                                                const selagdef = getAgencyDefaults(value);
                                                setFieldValue('Percentage', selagdef.Percentage);
                                                setFieldValue('OnGross', selagdef.OnGross);
                                            } else {
                                                setFieldValue('Percentage', 0);
                                                setFieldValue('OnGross', true);
                                            }
                                        }}
                                    />
                                )}
                                {/* Show Model dropdown in agency perspective when creating new */}
                                {isAgencyPerspective && !modelAgency && (
                                    <Field
                                        component={NWContactDropdown}
                                        label="Model"
                                        name="ModelID"
                                        activeTypes={['mod', 'sco']}
                                        value={values.ModelID}
                                        exclusions={excludedModels}
                                        onAfterChange={(value) => {
                                            const model = modelList.find((model) => model.ID === value);
                                            if (model && model.MAID) {
                                                setFieldValue('TypeName', 'Other');
                                                disableMother = true;
                                            } else {
                                                disableMother = false;
                                            }
                                        }}
                                    />
                                )}
                            </Col>
                            <Col lg={8} md={8} sm={12} xs={24}>
                                <Field
                                    component={NWSwitch}
                                    label="Dropped"
                                    name="Dropped"
                                    defaultChecked={initialValues.Dropped ? true : false}
                                    checked={Boolean(values.Dropped)}
                                    onAfterChange={(value) => {
                                        setIsDropped(value);
                                        if (value) {
                                            setFieldValue('Percentage', 0);
                                            setFieldValue('OnGross', true);
                                            if (!values.DateOut) {
                                                setFieldValue('DateOut', getMoment(new Date()));
                                            }
                                        } else {
                                            if (values.TypeName === 'Mother') {
                                                const selagdef = getAgencyDefaults(values.AgencyID);
                                                setFieldValue('Percentage', selagdef.Percentage);
                                                setFieldValue('OnGross', selagdef.OnGross);
                                            } else {
                                                setFieldValue('Percentage', 0);
                                                setFieldValue('OnGross', true);
                                            }
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                        {/* Towns */}
                        <Field
                            component={NWSelect}
                            label="Towns"
                            name="Towns"
                            mode="tags"
                            value={values.Towns}
                            manipulateValue={(val) => {
                                return val.map((v) => capitalize(v));
                            }}
                            options={cities.map((city) => {
                                return { key: city, label: city, value: city };
                            })}
                        />
                        <Row gutter={10}>
                            <Col lg={8} md={8} sm={12} xs={24}>
                                {/* DateIn */}
                                <Field
                                    component={NWDatePicker}
                                    label="Date In"
                                    name="DateIn"
                                    format="MMM DD, YYYY"
                                    style={{ width: '100%' }}
                                    value={values.DateIn ? getMoment(values.DateIn) : null}
                                    manipulateValue={datePickerBeforeOnChange}
                                />
                            </Col>
                            <Col lg={8} md={8} sm={12} xs={24}>
                                {/* DateOut */}
                                <Field
                                    component={NWDatePicker}
                                    label="Date Out"
                                    name="DateOut"
                                    format="MMM DD, YYYY"
                                    style={{ width: '100%' }}
                                    value={values.DateOut ? getMoment(values.DateOut) : null}
                                    manipulateValue={datePickerBeforeOnChange}
                                />
                            </Col>
                        </Row>
                        <Field
                            component={NWInput}
                            label="Booker"
                            name="Booker"
                            type="text"
                            style={{ width: '100%' }}
                            value={values.Booker}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Notes"
                            name="Notes"
                            type="text"
                            value={values.Notes}
                            maxLength={2000}
                            autoSize={{ minRows: 2, maxRows: 10 }}
                        />
                        {accessAccounting && (
                            <Field
                                component={NWTextAreaCharCounter}
                                label="Accounting Notes"
                                name="AccountingNotes"
                                type="text"
                                value={values.AccountingNotes}
                                maxLength={2000}
                                autoSize={{ minRows: 2, maxRows: 10 }}
                            />
                        )}
                        {values.TypeName !== 'Placement' ? (
                            <>
                                <NwDivider>Commission</NwDivider>
                                <Row gutter={10}>
                                    <Col lg={4} md={4} sm={12} xs={24}>
                                        {/* OnGross */}
                                        <Field
                                            component={NWSwitch}
                                            label="On Gross"
                                            name="OnGross"
                                            defaultChecked={initialValues.OnGross ? true : false}
                                            checked={Boolean(values.OnGross)}
                                            disabled={isDropped}
                                        />
                                    </Col>
                                    <Col lg={4} md={4} sm={12} xs={24}>
                                        {/* Percentage */}
                                        <Field
                                            component={NWInputNumber}
                                            label="Percentage"
                                            name="Percentage"
                                            min={0}
                                            max={100}
                                            step={0.01}
                                            value={values.Percentage}
                                            disabled={isDropped}
                                        />
                                    </Col>
                                    <Col lg={8} md={8} sm={12} xs={24} offset={4}>
                                        {/* ContractDate */}
                                        <Field
                                            style={{ width: '100%' }}
                                            component={NWDatePicker}
                                            label="Contract Date"
                                            name="ContractDate"
                                            format="MMM DD, YYYY"
                                            value={values.ContractDate ? getMoment(values.ContractDate) : null}
                                            manipulateValue={datePickerBeforeOnChange}
                                        />
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <>
                                <NwDivider>Contract</NwDivider>
                                <Row gutter={10}>
                                    <Col lg={24} md={24} sm={24} xs={24}>
                                        {/* ContractDate */}
                                        <Field
                                            style={{ width: '100%' }}
                                            component={NWDatePicker}
                                            label="Contract Date"
                                            name="ContractDate"
                                            format="MMM DD, YYYY"
                                            value={values.ContractDate ? getMoment(values.ContractDate) : null}
                                            manipulateValue={datePickerBeforeOnChange}
                                        />
                                    </Col>
                                </Row>
                            </>
                        )}
                        <br />
                        <NwFormButtonsBar
                            left={
                                <NwCancelButton
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        onTouchForm(false);
                                        onCancel();
                                    }}
                                />
                            }
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                        {/* <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
            &nbsp;
            <NwButton
              htmlType="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              label="Save"
              loadingLabel="Updating..."
            /> */}
                    </NwForm>
                )}
            </Formik>
        </div>
    );
};

export default ModelAgencyForm;
